import React from 'react';
import clsx from 'clsx';
import styles from 'components/AuthBackupMethodCard/styles';
import { FirstPartyDrawdownType } from 'components/Molecules/Drawdowns/FirstPartyCreateDrawdownDrawer/formData';
import { FormikProps } from 'formik';
import t from 'utils/translationHelper';

import { DrawdownFundingMethod } from '@alpha/fx-dtos';
import {
  Box,
  Divider,
  FormControlLabel, Radio, RadioGroup, Typography,
} from '@alpha/ui-lib/ui/external';
import { StyledKeyValueTable, TKeyValue } from '@alpha/ui-lib/ui/table';

import { formatNumber, getCurrencySymbol } from '../../../../../../utils/currency.helpers';
import Alert from '../../../../../Alert';
import { DrawdownRequestForm } from '../../formData';

import useStyles from './index.styles';

type Props = {
  drawdownForm: FormikProps<DrawdownRequestForm> | FormikProps<FirstPartyDrawdownType>,
  padAllowed?: boolean,
  defaultAccountName?: string,
  defaultAccountBalance?: number,
  defaultAccountCurrencyCode?: string,
  insufficientFunds: boolean,
}

const FundingMethod: React.FC<Props> = ({
  drawdownForm,
  padAllowed,
  defaultAccountName,
  defaultAccountBalance,
  defaultAccountCurrencyCode,
  insufficientFunds,
}) => {
  const classes = useStyles();

  const tableData = (fundingMethod: DrawdownFundingMethod): TKeyValue[] => {
    const data: TKeyValue[] = [];

    if (fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE) {
      data.push(
        {
          key: `${t('account_name')}:`,
          value: defaultAccountName || '-',
        },
        {
          key: `${t('account_balance')}:`,
          value: `${getCurrencySymbol(defaultAccountCurrencyCode)}${formatNumber(defaultAccountBalance, 2) || '-'} `,
        },
      );
    }

    return data;
  };
  return (
    <>
      <Box className={classes.card} style={{ marginTop: '25px' }}>
        <Box style={{ padding: '20px 20px 0px 20px' }}>
          <Typography style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>{t('funding_method')}</Typography>
          <RadioGroup
            row
            value={drawdownForm.values.fundingMethod}
            onChange={(e) => drawdownForm.setFieldValue('fundingMethod', e.target.value)}
            style={{ flexDirection: 'column' }}
          >
            <FormControlLabel
              value={DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE}
              control={<Radio color="primary" />}
              label={t('use_funds_on_account')}
              data-testid="funds-on-account-drawdown"
            />

            <FormControlLabel
              value={DrawdownFundingMethod.WIRE_TRANSFER}
              control={<Radio color="primary" />}
              label={t('wire_transfer_funds')}
              data-testid="wire-transfer-drawdown"
            />

            {padAllowed && (
              <FormControlLabel
                value={DrawdownFundingMethod.PAD}
                control={<Radio color="primary" />}
                label={t('settle_with_pad')}
                data-testid="pad-drawdown"
              />
            )}

          </RadioGroup>
        </Box>

        {tableData(drawdownForm.values.fundingMethod).length > 0 && (
          <Box>
            <Divider />
            <Box style={{ padding: '15px', backgroundColor: '#f7f7f7' }}>
              <StyledKeyValueTable
                testId="trade-funding-method-table"
                data={tableData(drawdownForm.values.fundingMethod)}
                className={clsx({
                  [classes.insufficientFundsTable]: insufficientFunds
                    && drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
                  [classes.sufficientFundsTable]: !insufficientFunds
                    && drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
                })}
              />
            </Box>
          </Box>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.WIRE_TRANSFER && (
          <div style={{ width: '100%', padding: '20px' }}>
            <Alert
              className={classes.alert}
              variant="info"
              text={t('details_of_where_to_send_the_settlement_will_be_displayed_on_the_final_screen')}
            />
          </div>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE
          && insufficientFunds && (
            <div style={{ width: '100%' }}>
              <Alert
                variant="warning"
                className={classes.insufficientFundsAlert}
                text={t('insufficient_funds')}
                subText={t('weve_noticed_insufficient_funds_to_perform_payment')}
              />
            </div>
        )}

        {drawdownForm.values.fundingMethod === DrawdownFundingMethod.PAD
         && (
         <div style={{ width: '10px auto', padding: '20px' }}>
           <Alert
             variant="info"
             className={classes.alert}
             text={t('pad_approved_drawdown')}
           />
         </div>
         )}
      </Box>
    </>
  );
};

export default FundingMethod;
